import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { CellContext } from "@tanstack/react-table";
import { Interview } from "@/services/interviewService";
import { Button } from "../../ui/button";
import {
  Upload,
  Loader2,
  AlertCircle,
  Eye,
  X,
  AlertTriangle,
} from "lucide-react";
import { TranscriptUploadModal } from "./TranscriptUploadModal";
import { transcriptService } from "../../../services/transcriptService";
import { toast } from "../../../hooks/use-toast";
import { track } from "@vercel/analytics";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

interface TranscriptStatusCellProps extends CellContext<Interview, unknown> {
  onUpdate?: (
    interviewId: string,
    field: keyof Interview,
    value: any,
    skipTranscriptModal?: boolean
  ) => void;
  onOpenChat?: (interviewId?: string) => void;
  onOpenTranscriptModal?: (args: {
    interview: Interview;
    mode: "upload" | "view";
    existingTranscript?: any | null;
  }) => void;
}

export interface TranscriptStatusCellRef {
  openUploadModal: () => void;
}

const TranscriptStatusCellComponent = forwardRef<
  TranscriptStatusCellRef,
  TranscriptStatusCellProps
>(({ row, onUpdate, onOpenChat, onOpenTranscriptModal }, ref) => {
  const interview = row.original;
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [modalMode, setModalMode] = useState<"upload" | "view">("upload");
  const [existingTranscript, setExistingTranscript] = useState<any>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  const transcriptStatus = interview.transcriptStatus;

  // Expose method to open upload modal via ref
  useImperativeHandle(ref, () => ({
    openUploadModal: () => {
      if (onOpenTranscriptModal) {
        onOpenTranscriptModal({ interview, mode: "upload" });
      } else {
        setModalMode("upload");
        setExistingTranscript(null);
        setIsCancelled(false);
        setIsUploadModalOpen(true);
      }
    },
  }));

  // Auto-refresh status when processing or cleaning (but not when cancelling)
  useEffect(() => {
    if (
      (transcriptStatus === "processing" || transcriptStatus === "cleaning") &&
      onUpdate &&
      !isCancelling &&
      !isCancelled
    ) {
      const checkStatus = async () => {
        try {
          const result = await transcriptService.getTranscript(interview.id);
          if (
            result.success &&
            result.transcript_status !== "processing" &&
            result.transcript_status !== "cleaning"
          ) {
            // Status changed, update the interview
            onUpdate(
              interview.id,
              "transcriptStatus",
              result.transcript_status
            );
          }
        } catch (error) {
          console.error("Failed to check transcript status:", error);
        }
      };

      // Check status more frequently for better responsiveness
      const interval = setInterval(checkStatus, 5000); // Every 5 seconds

      // Check once immediately after 2 seconds
      const timeout = setTimeout(checkStatus, 2000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [transcriptStatus, interview.id, onUpdate, isCancelling, isCancelled]);

  const handleUploadSuccess = () => {
    // Reset cancelled state for new upload
    setIsCancelled(false);
    // Refresh the interview data to show "processing" status
    if (onUpdate) {
      onUpdate(interview.id, "transcriptStatus", "processing");
      // Also update the interview status to completed (respecting follow-up status)
      // Pass skipTranscriptModal=true to avoid reopening the modal
      const newStatus =
        interview.status === "follow-up scheduled"
          ? "follow-up completed"
          : "completed";
      onUpdate(interview.id, "status", newStatus, true);
    }
  };

  const handleViewTranscript = async () => {
    setIsLoadingTranscript(true);
    try {
      const result = await transcriptService.getTranscript(interview.id);
      if (result.success && result.transcript) {
        // Track transcript view
        track("transcript_viewed", {
          interview_id: interview.id,
          has_cleaned_version: !!result.transcript.text_clean,
        });

        if (onOpenTranscriptModal) {
          onOpenTranscriptModal({
            interview,
            mode: "view",
            existingTranscript: result.transcript,
          });
        } else {
          setExistingTranscript(result.transcript);
          setModalMode("view");
          setIsUploadModalOpen(true);
        }
      }
    } catch (error) {
      console.error("Failed to load transcript:", error);
    } finally {
      setIsLoadingTranscript(false);
    }
  };

  const handleUploadClick = () => {
    if (onOpenTranscriptModal) {
      onOpenTranscriptModal({ interview, mode: "upload" });
    } else {
      setModalMode("upload");
      setExistingTranscript(null);
      setIsCancelled(false); // Reset cancelled state
      setIsUploadModalOpen(true);
    }
  };

  const handleCancelProcessing = async () => {
    // Immediately update UI state to stop polling and show cancelled state
    setIsCancelling(true);
    setIsCancelled(true);

    // Update UI immediately - don't wait for server response
    if (onUpdate) {
      onUpdate(interview.id, "transcriptStatus", null);
    }

    // Show immediate feedback
    toast({
      title: "Cancelling...",
      description: "Stopping transcript processing...",
    });

    try {
      // Send cancel request in background - don't wait for it
      const cancelPromise = transcriptService.cancelTranscript(interview.id);

      // Wait a short time to see if the cancel request completes quickly
      const result = await Promise.race([
        cancelPromise,
        new Promise<{ success: false; error: string }>((resolve) =>
          setTimeout(() => resolve({ success: false, error: "timeout" }), 2000)
        ),
      ]);

      if (result.success) {
        toast({
          title: "Processing Cancelled",
          description:
            "Transcript processing has been cancelled. You can now upload a new transcript.",
        });
      } else if (result.error !== "timeout") {
        console.error("Failed to cancel transcript:", result.error);
        toast({
          title: "Cancel May Have Failed",
          description:
            "The UI has been updated, but the server cancellation may not have completed.",
          variant: "destructive",
        });
      } else {
        // Timeout case - server is probably busy, but UI is already updated
        toast({
          title: "Processing Cancelled",
          description:
            "UI updated. Server cancellation is processing in background.",
        });

        // Continue trying to cancel in background without blocking UI
        cancelPromise
          .then((bgResult) => {
            if (!bgResult.success) {
              console.error("Background cancel failed:", bgResult.error);
            }
          })
          .catch((error) => {
            console.error("Background cancel error:", error);
          });
      }
    } catch (error) {
      console.error("Error cancelling transcript:", error);
      // Even if cancel fails, keep the UI updated since user clicked cancel
      toast({
        title: "Processing Cancelled",
        description:
          "UI updated. If processing continues, please try cancelling again.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="min-h-[28px] flex items-center gap-1 justify-between">
      {(transcriptStatus === "processing" || transcriptStatus === "cleaning") &&
      !isCancelled ? (
        <div className="flex items-center gap-1">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancelProcessing}
            disabled={isCancelling}
            className="h-6 w-6 p-0 rounded-full border border-muted-foreground/20 text-muted-foreground hover:border-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
            title="Cancel processing"
          >
            {isCancelling ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <X className="h-3 w-3" />
            )}
          </Button>
        </div>
      ) : transcriptStatus === "error" ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleUploadClick}
          className="h-6 px-2 text-xs gap-1 rounded-full border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950 dark:hover:border-red-600 dark:hover:text-red-400"
        >
          <AlertCircle className="h-3 w-3" />
          Retry
        </Button>
      ) : transcriptStatus === "ready" ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewTranscript}
          disabled={isLoadingTranscript}
          className="h-6 px-2 text-xs gap-1 rounded-full border border-muted-foreground/15 hover:bg-muted hover:border-muted-foreground/30"
        >
          {isLoadingTranscript ? (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          ) : (
            <Eye className="h-3 w-3 text-muted-foreground" />
          )}
          <span className="text-gray-900 dark:text-gray-100">View</span>
        </Button>
      ) : (
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUploadClick}
            className="h-6 px-2 text-xs gap-1 rounded-full border border-muted-foreground/20 hover:bg-muted hover:border-muted-foreground/40"
          >
            <Upload className="h-3 w-3 text-muted-foreground" />
            <span className="text-gray-900 dark:text-gray-100">Upload</span>
          </Button>
          {/* Show warning indicator for completed interviews without transcripts */}
          {(interview.status === "completed" ||
            interview.status === "follow-up completed") &&
            !transcriptStatus && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center cursor-default">
                      <AlertTriangle className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">
                      Missing transcript: Upload a transcript to make it
                      searchable
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
        </div>
      )}
      {/* Inline fallback modal only if no opener is provided */}
      {!onOpenTranscriptModal && (
        <TranscriptUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          interview={interview}
          onUploadSuccess={handleUploadSuccess}
          mode={modalMode}
          existingTranscript={existingTranscript}
          onOpenChat={onOpenChat}
        />
      )}
    </div>
  );
});

// Memoize the component to prevent unnecessary re-renders
// Only re-render if the interview ID, status, or transcriptStatus actually changes
export const TranscriptStatusCell = React.memo(
  TranscriptStatusCellComponent,
  (prevProps, nextProps) => {
    const prevInterview = prevProps.row.original;
    const nextInterview = nextProps.row.original;
    return (
      prevInterview.id === nextInterview.id &&
      prevInterview.status === nextInterview.status &&
      prevInterview.transcriptStatus === nextInterview.transcriptStatus &&
      prevProps.onUpdate === nextProps.onUpdate &&
      prevProps.onOpenChat === nextProps.onOpenChat &&
      prevProps.onOpenTranscriptModal === nextProps.onOpenTranscriptModal
    );
  }
);
